/**
 * Keeps Clinical Trials fast to reload.
 *
 * A reload forgets what the screen read, so My studies waited for the session
 * check, the investigators of the user's teams, the opening with its List
 * seed (the List's fields, workflow and layout) and the List's rows: about
 * five reads that share one backend process. The snapshot here keeps the last
 * of each this tab read live, per user, in memory and in sessionStorage. A
 * reload that holds one shows Clinical Trials before the session check
 * (ADR 0089) and paints My studies from it, then reads everything live behind
 * the screen and replaces it. Only a List grouped by chat paints from it:
 * grouped by status, the seed carries the rows, which come live. Home's
 * agenda (app/apps/hooks/workspaceAgendaCache.ts) and Calendar
 * (app/calendar/lib/calendarSnapshot.ts) do the same.
 *
 * The rows are taken from the List as the page goes away, so the snapshot
 * holds what the screen last showed, edits included.
 */
import type { ListRecord } from '@rudi/shared/types/lists';
import type { ListSeedPayload } from '@/platform/lists/lib/listIntentPreload';
import type { ClinicalTrialsViewersResponse } from '../hooks/useClinicalTrials';
import type { MyStudiesOpeningData } from './myStudiesApi';

/** A snapshot from another day still beats a skeleton; an older one does not. */
const SNAPSHOT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
/** Larger snapshots stay in memory only, so they cannot crowd sessionStorage. */
const SNAPSHOT_MAX_STORED_CHARS = 1_000_000;
const STORAGE_PREFIX = 'rudi-clinical-trials-snapshot:';

export type MyStudiesOpeningSnapshot = MyStudiesOpeningData & { seed: ListSeedPayload };

export interface ClinicalTrialsSnapshot {
  /** `/clinical-trials/viewers` as the backend answered it. */
  viewers: ClinicalTrialsViewersResponse | null;
  /** The opening without its rows; `seed` is the List seed it carried. */
  opening: MyStudiesOpeningSnapshot | null;
  /** Rows per status section the seed was read with. */
  seedPageSize: number;
  /** Every row of the opening's List, as the List last showed them. */
  rows: ListRecord[] | null;
  at: number;
}

const memory = new Map<string, ClinicalTrialsSnapshot>();
/** The List's rows as it shows them, per user, taken when the page goes away. */
const rowSources = new Map<string, () => ListRecord[] | null>();
let pagehideInstalled = false;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function isSnapshot(value: unknown): value is ClinicalTrialsSnapshot {
  if (!value || typeof value !== 'object') return false;
  const { viewers, opening, rows, seedPageSize, at } = value as Partial<ClinicalTrialsSnapshot>;
  return (
    typeof at === 'number' &&
    typeof seedPageSize === 'number' &&
    (viewers === null || Array.isArray(viewers?.viewers)) &&
    (opening === null ||
      (typeof opening?.listId === 'string' &&
        typeof opening.seed?.list?.id === 'string' &&
        Array.isArray(opening.seed.fields))) &&
    (rows === null || Array.isArray(rows))
  );
}

function store(userKey: string): void {
  const snapshot = memory.get(userKey);
  const target = storage();
  if (!snapshot || !target) return;
  try {
    const serialized = JSON.stringify(snapshot);
    if (serialized.length > SNAPSHOT_MAX_STORED_CHARS) target.removeItem(STORAGE_PREFIX + userKey);
    else target.setItem(STORAGE_PREFIX + userKey, serialized);
  } catch {
    // Quota or privacy mode: the in-memory copy still covers this document.
  }
}

/** Take the rows of every List still on screen and store each snapshot. */
function storeOnPagehide(): void {
  for (const [userKey, read] of rowSources) {
    const snapshot = memory.get(userKey);
    if (!snapshot?.opening) continue;
    try {
      const rows = read();
      if (rows) memory.set(userKey, { ...snapshot, rows });
    } catch {
      // The rows of the last store stay.
    }
  }
  for (const userKey of memory.keys()) store(userKey);
}

function installPagehide(): void {
  if (pagehideInstalled || typeof window === 'undefined') return;
  pagehideInstalled = true;
  window.addEventListener('pagehide', storeOnPagehide);
}

function update(
  userKey: string,
  now: Date,
  change: (previous: ClinicalTrialsSnapshot) => Partial<ClinicalTrialsSnapshot>
): void {
  const previous = readClinicalTrialsSnapshot(userKey, now) ?? {
    viewers: null,
    opening: null,
    seedPageSize: 0,
    rows: null,
    at: now.getTime(),
  };
  memory.set(userKey, { ...previous, ...change(previous), at: now.getTime() });
  installPagehide();
  store(userKey);
}

/** Keep the viewer list read live. */
export function rememberClinicalTrialsViewers(
  userKey: string,
  viewers: ClinicalTrialsViewersResponse,
  now: Date = new Date()
): void {
  update(userKey, now, () => ({ viewers }));
}

/**
 * Keep an opening read live. One without a seed updates the kept opening of
 * the same List and keeps its seed; a List still preparing its first sync has
 * nothing to paint and is not kept. The rows the opening carried, if any, are
 * kept until the List hands over the ones it shows.
 */
export function rememberMyStudiesSnapshot(
  userKey: string,
  opening: MyStudiesOpeningData & { seed?: ListSeedPayload | null; rows?: unknown },
  seedPageSize: number,
  now: Date = new Date()
): void {
  if (opening.syncState === 'preparing') return;
  const previous = readClinicalTrialsSnapshot(userKey, now);
  const sameList = previous?.opening?.listId === opening.listId;
  const seed = opening.seed ?? (sameList ? previous?.opening?.seed : null);
  if (!seed) return;
  const { rows, ...withoutRows } = opening;
  const items = (rows as { items?: unknown } | null | undefined)?.items;
  update(userKey, now, kept => ({
    opening: { ...withoutRows, seed },
    seedPageSize: opening.seed ? seedPageSize : kept.seedPageSize || seedPageSize,
    rows: Array.isArray(items) ? (items as ListRecord[]) : sameList ? kept.rows : null,
  }));
}

/**
 * Name where the rows the List shows come from, read when the page goes away.
 * Returns a function that withdraws it.
 */
export function keepMyStudiesRows(userKey: string, read: () => ListRecord[] | null): () => void {
  rowSources.set(userKey, read);
  installPagehide();
  return () => {
    if (rowSources.get(userKey) === read) rowSources.delete(userKey);
  };
}

export function readClinicalTrialsSnapshot(
  userKey: string,
  now: Date = new Date()
): ClinicalTrialsSnapshot | null {
  let snapshot = memory.get(userKey) ?? null;
  if (!snapshot) {
    try {
      const raw = storage()?.getItem(STORAGE_PREFIX + userKey);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      snapshot = isSnapshot(parsed) ? parsed : null;
    } catch {
      snapshot = null;
    }
    if (snapshot) memory.set(userKey, snapshot);
  }
  if (!snapshot || now.getTime() - snapshot.at > SNAPSHOT_MAX_AGE_MS) return null;
  return snapshot;
}

export interface MyStudiesSnapshotToPaint {
  opening: MyStudiesOpeningSnapshot;
  seedPageSize: number;
  rows: ListRecord[];
}

/**
 * The kept opening and rows to paint My studies from: only a List grouped by
 * chat reads its rows apart from the seed, so only it paints.
 */
export function myStudiesSnapshotToPaint(
  userKey: string,
  now: Date = new Date()
): MyStudiesSnapshotToPaint | null {
  const snapshot = readClinicalTrialsSnapshot(userKey, now);
  const opening = snapshot?.opening;
  if (!opening || !snapshot.rows || (opening.counts.notInChat ?? 0) <= 0) return null;
  return { opening, seedPageSize: snapshot.seedPageSize, rows: snapshot.rows };
}

/** The kept viewer list, as the backend answered it. */
export function viewersSnapshot(
  userKey: string,
  now: Date = new Date()
): ClinicalTrialsViewersResponse | null {
  return readClinicalTrialsSnapshot(userKey, now)?.viewers ?? null;
}

/** Whether a reload can paint My studies and its header from this tab alone. */
export function clinicalTrialsPaintsFromSnapshot(userKey: string, now: Date = new Date()): boolean {
  return viewersSnapshot(userKey, now) !== null && myStudiesSnapshotToPaint(userKey, now) !== null;
}

/** Drop the kept snapshots, for sign-out and tests. */
export function clearClinicalTrialsSnapshots(): void {
  memory.clear();
  rowSources.clear();
  const target = storage();
  if (!target) return;
  try {
    for (const key of [...Array(target.length).keys()].map(index => target.key(index))) {
      if (key?.startsWith(STORAGE_PREFIX)) target.removeItem(key);
    }
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
